// The patient directory.
//
// This screen used to read the standalone `patients` table, which nothing in this lab writes to —
// so it always said "No patients yet" while the lab was treating people. A patient here IS someone
// who appears on cases: their name, phone and doctor live on the case record, so the directory is
// derived from cases rather than kept as a second list that would immediately drift.
//
// Identity is (name + doctor group): the same name under two doctors is two people; one person with
// six cases is one row.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, type WfPatientSummary } from '@alignova/shared';
import { PageHeader, Card } from '../../ui/primitives.tsx';
import { SkeletonList } from '../../ui/mobile.tsx';
import { useLiveEvent } from '../../lib/useLive.ts';
import { IconChevronRight, IconSearch } from '../../ui/icons.tsx';
import { wfApi } from '../workflow/workflowApi.ts';
import { Empty } from '../workflow/wfUi.tsx';

export function PatientsPage() {
  const [items, setItems] = useState<WfPatientSummary[] | null>(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    wfApi
      .patients(q.trim() || undefined)
      .then((r) => {
        setItems(r);
        setErr(null);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useLiveEvent('workflow.changed', load);

  return (
    <div>
      <PageHeader
        title="Patients"
        subtitle="Everyone the lab is treating, gathered from their cases."
        action={<span className="tabnum text-sm text-muted">{items?.length ?? 0} patients</span>}
      />

      <div className="relative mb-3 max-w-md">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">
          <IconSearch size={16} />
        </span>
        <input
          className="h-9 w-full rounded-md border border-line bg-surface-2/60 pl-9 pr-3 text-sm"
          placeholder="Search by name, phone or group ID…"
          aria-label="Search patients"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {err && <p className="mb-3 text-sm text-status-urgent-fg">{err}</p>}
      {!items && <SkeletonList rows={5} />}
      {items && !items.length && (
        <Empty>
          {q ? 'Nobody matches that search.' : 'No patients yet - they appear here as cases come in.'}
        </Empty>
      )}

      {items && items.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-surface-2">
              <tr>
                {['Patient', 'Phone', 'Doctor', 'Cases', 'Last case', ''].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left text-[12px] font-semibold text-muted">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={`${p.nameNorm}:${p.doctorId ?? ''}`} className="border-t border-line transition-colors hover:bg-surface-2/50">
                  <td className="px-3 py-2.5 font-medium text-ink">{p.name}</td>
                  <td className="tabnum whitespace-nowrap px-3 py-2.5 text-muted">{p.phone || '-'}</td>
                  <td className="px-3 py-2.5">
                    <div className="truncate">{p.doctorName ?? p.clinicName ?? '-'}</div>
                    {p.groupId && <div className="font-mono text-[11px] text-muted">{p.groupId}</div>}
                  </td>
                  <td className="tabnum px-3 py-2.5">{p.caseCount}</td>
                  <td className="tabnum whitespace-nowrap px-3 py-2.5 text-muted">{formatDate(p.lastReceivedAt)}</td>
                  <td className="px-3 py-2.5 text-right">
                    {/* Their cases, not a patient record: the case is where everything about them is. */}
                    <Link
                      to={`/workflow/cases?search=${encodeURIComponent(p.name)}&includeTerminal=1`}
                      className="tap inline-flex items-center gap-1 rounded-md border border-line px-2.5 py-1 text-xs font-medium text-ink hover:bg-surface-2"
                    >
                      Cases
                      <IconChevronRight size={14} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
